/*
 * "Insert a link to another article".
 *
 * Inserts the id form of the token - [[kb:<guid>]] - never the slug form, because
 * the id is the only reference that survives a slug being edited. The slug forms
 * exist for hand-written markdown; the picker has no reason to produce something
 * more fragile.
 *
 * The list is the admin article list, so an article that is not published yet can be
 * linked to. That is the normal case when writing a set of related articles at once:
 * the link is written now and starts working for readers when the target is
 * published, and the editor's preview says which links are in that state.
 */

import { useState } from 'react';
import { buildArticleLinkToken } from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAsync, useDebounced } from '../../hooks/useAsync.js';
import { ErrorBanner, Loading, Modal, PublishingStatusBadge } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';

export function ArticlePickerDialog({
  open,
  onClose,
  onInsert,
  excludeArticleId,
  noun = 'article',
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (token: string) => void;
  excludeArticleId?: string;
  /**
   * What the token is being written into - 'article' (the default, every
   * string as it always was) or 'release note' (S42), which links to an
   * article rather than to "another" one.
   */
  noun?: string;
}): JSX.Element | null {
  // Its words: t.admin.editor.articlePicker (S48).
  const words = useMessages().admin.editor.articlePicker;
  const api = useAdminApi();
  const [searchDraft, setSearchDraft] = useState('');
  const [linkText, setLinkText] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const search = useDebounced(searchDraft, 300);

  const state = useAsync(
    (signal) =>
      open
        ? api.listArticles(
            {
              q: search,
              products: [],
              categories: [],
              status: 'all',
              page: 1,
              pageSize: 25,
              sort: 'modified',
            },
            signal,
          )
        : Promise.resolve(null),
    [open, search],
  );

  if (!open) return null;

  const items = (state.data?.items ?? []).filter(
    (article) => article.articleId !== excludeArticleId,
  );

  const insert = () => {
    if (!selected) return;
    onInsert(
      buildArticleLinkToken(selected, linkText.trim() ? { text: linkText.trim() } : {}),
    );
    setSelected(null);
    setLinkText('');
    setSearchDraft('');
    onClose();
  };

  return (
    <Modal
      open
      wide
      title={words.title(noun)}
      onClose={onClose}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto' }}>
            {words.tokenHint(noun)}
          </span>
          <button type="button" className="button" onClick={onClose}>
            {words.cancel}
          </button>
          <button
            type="button"
            className="button button-primary"
            onClick={insert}
            disabled={!selected}
          >
            {words.insertLink}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="picker-search">{words.findLabel}</label>
        <input
          id="picker-search"
          type="search"
          /*
            data-autofocus, not autoFocus: React's prop focuses imperatively
            while this dialog is still display:none, so it does nothing at all.
            Modal focuses this element after showModal instead.
          */
          data-autofocus
          placeholder={words.findPlaceholder}
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
        />
      </div>

      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {state.loading && !state.data && <Loading label={words.searching} />}

      {state.data && items.length === 0 && <p className="muted">{words.noMatch}</p>}

      {items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '20rem', overflowY: 'auto' }}>
          {items.map((article) => {
            const isSelected = selected === article.articleId;
            return (
              <li key={article.articleId}>
                <label
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'flex-start',
                    padding: '0.5rem',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    background: isSelected ? 'var(--accent-soft)' : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="article"
                    checked={isSelected}
                    onChange={() => setSelected(article.articleId)}
                    style={{ marginTop: 4 }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block' }}>
                      {article.title ?? words.untitled}
                    </strong>
                    <span className="faint">
                      <code>{article.slug}</code>
                    </span>
                    <span style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
                      <PublishingStatusBadge status={article.status} />
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label htmlFor="picker-text">{words.linkTextLabel}</label>
        <input
          id="picker-text"
          type="text"
          placeholder={words.linkTextPlaceholder}
          value={linkText}
          onChange={(event) => setLinkText(event.target.value)}
        />
        <p className="field-hint">{words.linkTextHint}</p>
      </div>
    </Modal>
  );
}
